import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Grid2 as Grid, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  CircularProgress, Alert, Chip, Collapse, IconButton,
} from '@mui/material';
import {
  ReceiptLong as FechoIcon, Refresh as RefreshIcon,
  KeyboardArrowDown, KeyboardArrowUp,
} from '@mui/icons-material';
import { posAdminService, PosFecho } from '../../services/pos-admin.service';
import { dayCv, todayCv } from '../../utils/date';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;

function Linha({ f }: { f: PosFecho }) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setAberto(!aberto)}>
            {aberto ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {new Date(f.fechoAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{f.referencia}</TableCell>
        <TableCell sx={{ fontSize: 13 }}>{f.terminalNome ?? '—'}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{f.totalCount}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {cve(f.totalCve)}
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cve(f.taxaPagali)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cve(f.liquidoComerciante)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
          <Collapse in={aberto} unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Grid container spacing={2}>
                {[
                  { l: 'QR / SISP', c: f.qr.count, v: f.qr.total },
                  { l: 'NFC', c: f.nfc.count, v: f.nfc.total },
                  { l: 'Dinheiro (venda)', c: f.cash.count, v: f.cash.total },
                  { l: 'Cantina', c: f.cantina.count, v: f.cantina.total },
                ].map((x) => (
                  <Grid key={x.l} size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">{x.l}</Typography>
                    <Typography variant="body2" fontWeight={600}>{cve(x.v)}</Typography>
                    <Typography variant="caption" color="text.secondary">{x.c} operação(ões)</Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Carregamentos em numerário: o bruto é o que tem de estar na
                  gaveta; o líquido é o que foi para as carteiras dos clientes. */}
              {f.numerario.count > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Carregamentos em numerário
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Recebido em mão</Typography>
                      <Typography variant="body2" fontWeight={700}>{cve(f.numerario.bruto)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Taxa</Typography>
                      <Typography variant="body2">{cve(f.numerario.taxa)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Creditado</Typography>
                      <Typography variant="body2">{cve(f.numerario.liquido)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {f.notas && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>Notas:</strong> {f.notas}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** Fechos de caixa dos terminais POS. */
export default function PosFechos() {
  const hoje = todayCv();
  const ha30 = dayCv(new Date(Date.now() - 30 * 864e5));

  const [linhas, setLinhas] = useState<PosFecho[]>([]);
  const [totais, setTotais] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [from, setFrom] = useState(ha30);
  const [to, setTo] = useState(hoje);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await posAdminService.fechos({ from, to, page: page + 1, limit });
      setLinhas(r.linhas);
      setTotais(r.totais);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível carregar os fechos.');
    } finally {
      setLoading(false);
    }
  }, [from, to, page, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <FechoIcon color="primary" /> Fechos de Caixa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Conferência de turno por terminal, com o numerário recebido à parte.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {totais && (
        <Grid container spacing={2}>
          {[
            { l: 'Fechos', v: String(totais.fechos) },
            { l: 'Total movimentado', v: cve(totais.total), d: true },
            { l: 'Taxa Pagali', v: cve(totais.taxa) },
            { l: 'Líquido comerciante', v: cve(totais.liquido) },
            { l: 'Numerário recebido', v: cve(totais.numerario) },
          ].map((c) => (
            <Grid key={c.l} size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Card variant="outlined" sx={c.d ? { bgcolor: 'primary.50' } : undefined}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">{c.l}</Typography>
                  <Typography variant="h6" fontWeight={700}
                    color={c.d ? 'primary.main' : 'text.primary'}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {c.v}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
                value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
                value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : linhas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Sem fechos no período. Os fechos são criados pela aplicação POS no fim de cada turno.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Fecho</TableCell>
                    <TableCell>Referência</TableCell>
                    <TableCell>Terminal</TableCell>
                    <TableCell align="right">Operações</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                    <TableCell align="right">Líquido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((f) => <Linha key={f.id} f={f} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totais?.fechos ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Linhas"
          />
        </CardContent>
      </Card>
    </Box>
  );
}
